import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState('booting'); // 'booting' | 'authenticated' | 'unauthenticated'
  const [loading, setLoading] = useState(true);

  // Clear auth data
  const clearAuth = useCallback(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  // Boot: validate token on app start/refresh
  const boot = useCallback(async () => {
    setStatus('booting');
    setLoading(true);
    
    const token = localStorage.getItem('access_token');
    const storedUser = localStorage.getItem('user');
    
    // No token = not authenticated
    if (!token || token === 'undefined' || token === 'null') {
      clearAuth();
      setLoading(false);
      return;
    }
    
    // Try to validate token by calling /auth/me
    try {
      const response = await authAPI.getProfile();
      const userData = response?.data?.data || response?.data;
      
      if (userData && userData.id) {
        // Token is valid, set user
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        setStatus('authenticated');
      } else {
        // Invalid response, clear auth
        clearAuth();
      }
    } catch (error) {
      console.error('Error validating token:', error);
      // Token invalid or expired, try refresh
      const refreshToken = localStorage.getItem('refresh_token');
      
      if (refreshToken && refreshToken !== 'undefined' && refreshToken !== 'null') {
        try {
          const refreshResponse = await authAPI.refresh(refreshToken);
          const { access_token, refresh_token: newRefreshToken, user: userData } = refreshResponse.data;
          
          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', newRefreshToken);
          localStorage.setItem('user', JSON.stringify(userData));
          setUser(userData);
          setStatus('authenticated');
        } catch (refreshError) {
          console.error('Error refreshing token:', refreshError);
          clearAuth();
        }
      } else {
        // No refresh token, clear auth
        clearAuth();
      }
    } finally {
      setLoading(false);
    }
  }, [clearAuth]);

  // Boot on mount
  useEffect(() => {
    boot();
  }, [boot]);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    
    // Verificar que la respuesta tenga los datos esperados
    if (!response.data || !response.data.access_token) {
      throw new Error('Respuesta de login inválida');
    }
    
    const { access_token, refresh_token, user: userData } = response.data;
    
    // Guardar tokens y usuario
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('user', JSON.stringify(userData));
    
    // Set user and status
    setUser(userData);
    setStatus('authenticated');
    
    return userData;
  };

  const register = async (userData) => {
    const response = await authAPI.register(userData);
    
    if (!response.data || !response.data.access_token) {
      throw new Error('Respuesta de registro inválida');
    }
    
    const { access_token, refresh_token, user: newUser } = response.data;
    
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('user', JSON.stringify(newUser));
    
    setUser(newUser);
    setStatus('authenticated');
    
    return newUser;
  };

  const logout = useCallback(() => {
    clearAuth();
  }, [clearAuth]);

  const updateUser = (updatedData) => {
    const newUser = { ...user, ...updatedData };
    localStorage.setItem('user', JSON.stringify(newUser));
    setUser(newUser);
  };

  const refreshToken = useCallback(async () => {
    const refreshTokenValue = localStorage.getItem('refresh_token');
    
    if (!refreshTokenValue || refreshTokenValue === 'undefined' || refreshTokenValue === 'null') {
      throw new Error('No refresh token available');
    }
    
    try {
      const response = await authAPI.refresh(refreshTokenValue);
      const { access_token, refresh_token: newRefreshToken, user: userData } = response.data;
      
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', newRefreshToken);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      setStatus('authenticated');
      
      return access_token;
    } catch (error) {
      console.error('Error refreshing token:', error);
      clearAuth();
      throw error;
    }
  }, [clearAuth]);

  const isDeveloper = () => user?.role === 'desarrollador';
  const isConstructor = () => user?.role === 'constructor';

  const value = {
    user,
    loading,
    status, // 'booting' | 'authenticated' | 'unauthenticated'
    login,
    register,
    logout,
    updateUser,
    refreshToken,
    boot,
    isDeveloper,
    isConstructor,
    isAuthenticated: status === 'authenticated',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
